import React, { useMemo, useState } from 'react'
import axios from 'axios'
import { Link as RouterLink } from 'react-router-dom'
import { baseUrl } from 'features/constants'
import { legalCss } from './legal.styles'
import { useTranslation } from 'react-i18next'

const pageCss = `
.delete-layout {
  max-width: 980px;
  margin: 0 auto;
  padding: 56px 24px 120px;
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(320px, 1.1fr);
  gap: 32px;
  align-items: start;
}
.delete-panel {
  background: #ffffff;
  border: 1px solid #ececdf;
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(20, 24, 11, 0.04);
  padding: 28px;
}
.delete-panel h2 {
  margin: 0 0 14px;
  color: #14180b;
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: 0;
}
.delete-panel p {
  color: #4a4f3d;
  font-size: 15px;
  line-height: 1.65;
}
.delete-panel ul {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 22px;
}
.delete-panel li {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 12px;
  color: #2c2f23;
  font-size: 14.5px;
  line-height: 1.55;
}
.delete-panel li::before {
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6f952e;
  box-shadow: 0 0 0 7px rgba(111, 149, 46, 0.12);
  margin: 8px 0 0 8px;
}
.delete-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.delete-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.delete-field label {
  color: #1a1d12;
  font-weight: 650;
  font-size: 13px;
}
.delete-field input,
.delete-field select,
.delete-field textarea {
  width: 100%;
  border: 1px solid #dfe2d4;
  border-radius: 12px;
  background: #fbfbf8;
  color: #14180b;
  font: inherit;
  font-size: 15px;
  padding: 13px 14px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.delete-field textarea {
  min-height: 130px;
  resize: vertical;
}
.delete-field input:focus,
.delete-field select:focus,
.delete-field textarea:focus {
  border-color: #6f952e;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(111, 149, 46, 0.12);
}
.delete-submit {
  appearance: none;
  border: 0;
  border-radius: 999px;
  background: #6f952e;
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  padding: 14px 22px;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease, opacity 0.15s ease;
}
.delete-submit:hover {
  background: #5f8326;
  transform: translateY(-1px);
}
.delete-submit:disabled {
  cursor: not-allowed;
  opacity: 0.65;
  transform: none;
}
.delete-status {
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.55;
}
.delete-status.success {
  background: rgba(111, 149, 46, 0.1);
  color: #345111;
  border: 1px solid rgba(111, 149, 46, 0.24);
}
.delete-status.error {
  background: rgba(184, 58, 58, 0.1);
  color: #7a2323;
  border: 1px solid rgba(184, 58, 58, 0.24);
}
.delete-note {
  color: #6b6f5d;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
}
@media (max-width: 840px) {
  .delete-layout {
    grid-template-columns: 1fr;
    padding: 32px 20px 88px;
  }
}
`

const initialForm = {
  name: '',
  email: '',
  accountType: '',
  details: '',
}

export default function DeleteDataRequest({ mode = 'data' }) {
  const { t } = useTranslation('about')
  const [form, setForm] = useState(initialForm)
  const [status, setStatus] = useState({ type: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const content = useMemo(() => {
    const isAccount = mode === 'account'

    return {
      isAccount,
      eyebrow: t(isAccount ? 'deletion.account.eyebrow' : 'deletion.data.eyebrow'),
      title: t(isAccount ? 'deletion.account.title' : 'deletion.data.title'),
      subtitle: t(isAccount ? 'deletion.account.subtitle' : 'deletion.data.subtitle'),
      requestType: isAccount ? 'account-deletion' : 'data-deletion',
      buttonText: t(isAccount ? 'deletion.account.submit' : 'deletion.data.submit'),
      helpTitle: t(isAccount ? 'deletion.account.helpTitle' : 'deletion.data.helpTitle'),
      helpItems: t(isAccount ? 'deletion.account.helpItems' : 'deletion.data.helpItems', { returnObjects: true }),
    }
  }, [mode, t])

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm((current) => ({
      ...current,
      [name]: value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setStatus({ type: '', message: '' })
    setIsSubmitting(true)

    try {
      await axios.post(`${baseUrl}/api/reportIssue/createDeletionRequest`, {
        requestType: content.requestType,
        email: form.email,
        accountType: form.accountType,
        name: form.name,
        details: form.details,
      })

      setForm(initialForm)
      setStatus({
        type: 'success',
        message: t('deletion.success'),
      })
    } catch (error) {
      setStatus({
        type: 'error',
        message: error?.response?.data?.message || t('deletion.error'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='lgl'>
      <style>{legalCss}</style>
      <style>{pageCss}</style>

      <div className='lgl-topbar'>
        <RouterLink to='/'>Praysely</RouterLink>
        <nav>
          <RouterLink to='/delete-my-account' className={content.isAccount ? 'is-active' : ''}>{t('deletion.nav.account')}</RouterLink>
          <RouterLink to='/delete-my-data' className={!content.isAccount ? 'is-active' : ''}>{t('deletion.nav.data')}</RouterLink>
          <RouterLink to='/info/privacy'>{t('deletion.nav.privacy')}</RouterLink>
        </nav>
      </div>

      <header className='lgl-hero'>
        <div className='lgl-eyebrow'>{content.eyebrow}</div>
        <h1 className='lgl-title'>{content.title}</h1>
        <p className='lgl-subtitle'>{content.subtitle}</p>
        <div className='lgl-updated'>{t('deletion.reviewed')}</div>
      </header>

      <main className='delete-layout'>
        <section className='delete-panel'>
          <h2>{content.helpTitle}</h2>
          <p>
            {t('deletion.instructions')}
          </p>
          <ul>
            {content.helpItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className='delete-panel'>
          <form className='delete-form' onSubmit={handleSubmit}>
            <div className='delete-field'>
              <label htmlFor='name'>{t('deletion.form.name')}</label>
              <input
                id='name'
                name='name'
                value={form.name}
                onChange={handleChange}
                placeholder={t('deletion.form.namePlaceholder')}
                autoComplete='name'
              />
            </div>

            <div className='delete-field'>
              <label htmlFor='email'>{t('deletion.form.email')}</label>
              <input
                id='email'
                name='email'
                type='email'
                value={form.email}
                onChange={handleChange}
                placeholder='you@example.com'
                autoComplete='email'
                required
              />
            </div>

            <div className='delete-field'>
              <label htmlFor='accountType'>{t('deletion.form.accountType')}</label>
              <select
                id='accountType'
                name='accountType'
                value={form.accountType}
                onChange={handleChange}
              >
                <option value=''>{t('deletion.form.select')}</option>
                <option value='member'>{t('deletion.form.member')}</option>
                <option value='church'>{t('deletion.form.church')}</option>
                <option value='not-sure'>{t('deletion.form.notSure')}</option>
              </select>
            </div>

            <div className='delete-field'>
              <label htmlFor='details'>{t('deletion.form.details')}</label>
              <textarea
                id='details'
                name='details'
                value={form.details}
                onChange={handleChange}
                placeholder={t('deletion.form.detailsPlaceholder')}
              />
            </div>

            {status.message && (
              <div className={`delete-status ${status.type}`} role='status'>
                {status.message}
              </div>
            )}

            <button className='delete-submit' type='submit' disabled={isSubmitting}>
              {isSubmitting ? t('deletion.form.submitting') : content.buttonText}
            </button>

            <p className='delete-note'>
              {t('deletion.note')}
            </p>
          </form>
        </section>
      </main>
    </div>
  )
}
